/* Two DOM enhancements to Appstle's Autoship widget, both applied via
   the same MutationObserver since Appstle renders (and re-renders) its
   widget markup asynchronously, well after DOMContentLoaded fires — a
   one-time querySelector at load time finds nothing.

   1) Hide "Why customers love Autoship" (.appstle_tooltip_wrapper_static,
      merchant-configured) — its content is surfaced directly in the
      Autoship & Save box instead (see llmInjectAutoshipBenefits), so
      the box itself is just hidden rather than exposed via a toggle.

   2) Convert "Deliver Every:" (.appstle_subscribe_option_grid, up to 12
      stacked radio options — every product on this store uses the same
      12 frequency choices) into a real <select> dropdown. The native
      radios are kept in the DOM (hidden, not removed) so Appstle's own
      click-driven logic keeps working exactly as before — the
      dropdown's change handler just calls .click() on the matching
      radio rather than reimplementing any selection/pricing logic. */

/* Real change, per owner's ask: "Why choose Autoship?" (a toggle
   button opening a modal with Appstle's own tooltip copy) is now
   redundant — that same copy is shown directly in the box via
   llmInjectAutoshipBenefits, so the separate toggle/modal was
   removed entirely rather than left as a duplicate path to the same
   information. The tooltip box itself stays hidden, since
   llmInjectAutoshipBenefits still reads its content directly. */
function llmSetupAutoshipToggle(box) {
  box.style.display = 'none';
}

function llmSetupDeliveryDropdown(grid) {
  if (grid.dataset.llmDropdownReady) return;

  const radioWrappers = grid.querySelectorAll('.appstle-radio-input-wrapper');
  const radios = grid.querySelectorAll('input[type="radio"]');
  if (!radioWrappers.length || !radios.length) return;

  grid.dataset.llmDropdownReady = 'true';

  const select = document.createElement('select');
  select.className = 'llm-autoship-frequency-select';

  radioWrappers.forEach(function (wrapper) {
    const radio = wrapper.querySelector('input[type="radio"]');
    if (!radio) return;
    const option = document.createElement('option');
    option.value = radio.id;
    option.textContent = wrapper.textContent.trim();
    if (radio.checked) option.selected = true;
    select.appendChild(option);

    wrapper.style.display = 'none';
  });

  select.addEventListener('change', function () {
    const radio = document.getElementById(select.value);
    if (radio) radio.click();
  });

  grid.appendChild(select);
}

/* Appstle's own hardcoded label text reads "Autoship& Save" — no space
   before the ampersand. This is content, not styling, so a CSS rule
   can't fix it; patches the actual text node instead. Only touches
   elements with exactly that text so it can't accidentally rewrite
   something else if Appstle changes the copy later. */
function llmFixAutoshipLabelSpacing(el) {
  /* Real bug found: this used to gate on a one-time
     dataset.llmSpacingFixed flag, assuming the fix only needed to run
     once. It didn't — Appstle's widget re-renders this text node on
     its own (confirmed: the broken spacing came back after switching
     between purchase options), and the stale flag then skipped
     re-checking it, silently leaving the reverted text broken. Safe
     to just re-run on every scan instead: the regex only matches the
     BROKEN form (no space before "&"), and the fixed text ("Autoship
     & Save", space before it) never matches it again, so this is
     naturally idempotent without needing a flag at all. */
  /* Real bug: the actual character before "Save" is a non-breaking
     space (char code 160,  ), not a regular space (code 32) — a
     strict === comparison against a regular-space string never
     matched, even though the text looked identical in every console
     log/inspection. Using a whitespace-agnostic regex instead. */
  if (/^Autoship&\s*Save$/.test(el.textContent.trim())) {
    el.textContent = 'Autoship & Save';
  }
}

/* Owner asked for the "SAVE X%" badge to sit under the price instead
   of next to the "Autoship & Save" heading (Carewell-style). Real DOM
   structure: the badge lives nested inside .appstle-text-and-badge-
   wrapper, itself inside .appstle_radio_label — a completely
   different parent from .appstle_subscription_amount_wrapper (the
   price), which is a SIBLING of the label, not a descendant of it. A
   CSS order/position change alone can't move an element across
   different flex containers, so this actually relocates the node. */
function llmMoveSaveBadgeUnderPrice(badge) {
  if (badge.dataset.llmMoved) return;
  const priceWrapper = document.querySelector('.appstle_subscription_amount_wrapper');
  if (!priceWrapper) return;
  priceWrapper.appendChild(badge);
  badge.dataset.llmMoved = 'true';
}

/* Owner asked to make the Autoship & Save box more enticing,
   Chewy/Carewell/Amazon-style — a recognizable "autoship" icon next
   to the label, plus the actual benefit copy visible in the box
   itself instead of hidden behind the "Why choose Autoship?" click.
   Reuses the real merchant-configured bullets already sitting in
   .appstle_tooltip_wrapper_static (the same content the modal shows)
   rather than inventing separate marketing copy that could drift out
   of sync with whatever the merchant edits in Appstle's own settings. */
function llmInjectAutoshipIcon(labelTextEl) {
  if (labelTextEl.dataset.llmIconAdded) return;
  labelTextEl.dataset.llmIconAdded = 'true';
  const icon = document.createElement('span');
  icon.className = 'llm-autoship-icon';
  /* The canonical Autoship mark — geometry mirrors snippets/llm-icon-autoship.liquid. This is
     injected into Appstle's widget at runtime so it cannot {% render %} the snippet; keep the two
     in step if the mark is ever redrawn.

     Until 2026-09-07 this was a DIFFERENT drawing from the snippet (r=6 arcs, stroke 1.4, no
     arrowheads) — exactly the kind of quiet divergence that snippet exists to prevent — and thin
     enough at 15px that the owner read it as missing from the buy box entirely. */
  icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M4.75 8.62A8 8 0 0 1 18.55 7.41" stroke="var(--llm-as-lead, #06386b)" stroke-width="2.4" stroke-linecap="round"/>' +
    '<path d="M21.71 11.92 15.93 9.25 21.17 5.57Z" fill="var(--llm-as-lead, #06386b)"/>' +
    '<path d="M19.25 15.38A8 8 0 0 1 5.45 16.59" stroke="var(--llm-as-trail, #ff4438)" stroke-width="2.4" stroke-linecap="round"/>' +
    '<path d="M2.29 12.08 8.07 14.75 2.83 18.43Z" fill="var(--llm-as-trail, #ff4438)"/>' +
    '</svg>';
  labelTextEl.insertBefore(icon, labelTextEl.firstChild);
}

/* Owner asked to drop "Never run out" specifically — 4 bullets pushed
   Add to Cart further down than needed, and the other 3 (save money,
   full control, cancel anytime) were judged sufficient on their own
   to sell the option. Matched by its bold lead-in text rather than
   position, so it stays correctly excluded even if the merchant
   reorders the bullets in Appstle's own tooltip settings later. */
const LLM_AUTOSHIP_BENEFIT_EXCLUDE = ['never run out'];

function llmInjectAutoshipBenefits(card, tooltipBox) {
  if (card.querySelector('.llm-autoship-benefits')) return;
  if (!tooltipBox) return;
  const items = tooltipBox.querySelectorAll('li');
  if (!items.length) return;
  const anchor = card.querySelector('.appstle_subscription_amount_wrapper');
  if (!anchor) return;

  /* Owner spotted a double-checkmark on every bullet — the original
     Appstle tooltip li's ✅ sits INSIDE its own <b> tag
     (`<b>✅ Save more</b> — ...`), confirmed via the live innerHTML.
     The old regex was anchored to the very start of the whole
     innerHTML string (`^\s*...`), which only ever matches text
     coming before any tag — since the string actually starts with
     `<b>`, the emoji right after it was never touched, and this
     list's own CSS ::before checkmark-circle icon then rendered
     alongside the untouched original emoji. Matching right after the
     start OR after any opening tag's `>` (not just the absolute
     string start) strips it regardless of which element wraps it. */
  const list = document.createElement('ul');
  list.className = 'llm-autoship-benefits';
  items.forEach(function (li) {
    const leadIn = (li.querySelector('b') ? li.querySelector('b').textContent : li.textContent)
      .replace(/(^|>)\s*(✅|✓)\s*/, '$1').trim().toLowerCase();
    if (LLM_AUTOSHIP_BENEFIT_EXCLUDE.indexOf(leadIn) !== -1) return;
    const item = document.createElement('li');
    item.innerHTML = li.innerHTML.replace(/(^|>)\s*(✅|✓)\s*/, '$1');
    list.appendChild(item);
  });
  anchor.insertAdjacentElement('afterend', list);
}

/* Owner asked to incorporate the "Ships Monday to..."/"Estimated
   delivery" lines into each purchase-option box (Carewell/Chewy/
   Amazon-style) instead of showing them as their own block in the
   middle column — see llm-product-shipping.css for where that
   standalone block gets hidden. This injects one mount div per
   option card, using the SAME .custom_message class the existing
   shipping-ETA script (llm-shipping-eta.js) already targets
   (querySelectorAll("#custom_message, .custom_message") — confirmed
   by reading that file directly), so its own 1-second re-render
   interval picks up and populates BOTH new mounts automatically, no
   changes to that script needed. */
function llmInjectOptionDelivery(card, afterSelector) {
  if (card.querySelector('.llm-option-delivery')) return;
  const anchor = card.querySelector(afterSelector);
  if (!anchor) return;
  const mount = document.createElement('div');
  mount.className = 'custom_message llm-option-delivery';
  anchor.insertAdjacentElement('afterend', mount);

  /* Real bug found: Appstle's own widget re-renders the option cards
     wholesale on every variant change (its price differs per
     variant), wiping this mount out along with everything else. This
     function re-creates it on the next scan, but it started out
     EMPTY — the actual "Ships Monday..."/"Estimated delivery" text
     only got filled in by llm-shipping-eta.js's own periodic
     re-render, which runs on a 1-second interval, not on every DOM
     change. That up-to-1-second gap of an empty mount is what read as
     the delivery estimate "disappearing and reappearing" on every
     variant switch. Calling the exposed render function immediately
     closes that gap instead of waiting for the next interval tick. */
  if (typeof window.llmRenderShippingMessages === 'function') {
    window.llmRenderShippingMessages();
  }
}

/* Stock indicator in the buy boxes (Amazon/Carewell-style green check + text) reflecting the SELECTED
   variant's real inventory: "In stock", or "Low stock (N units)" / "Very low stock (N units)" when
   low, and NOTHING for untracked (made-to-order) or out-of-stock variants — matching the product-card
   logic + the same theme settings. Per-variant data comes from #llm-variant-inventory-data
   (id → {q: qty, t: tracked}) rendered by sections/main-product.liquid, because on:variant:change
   carries only the variant id, not its inventory_quantity. Updates BOTH the standalone (no-sub) badge
   and the Appstle-card badge (which Appstle wipes on every re-render, so the scan re-applies it). */
let llmInvData = {};
let llmInvCfg = { veryLow: 8, low: 20, showCount: 'low' };
(function () {
  try {
    const d = document.getElementById('llm-variant-inventory-data');
    if (d) llmInvData = JSON.parse(d.textContent);
    const c = document.getElementById('llm-inventory-config');
    if (c) llmInvCfg = JSON.parse(c.textContent);
  } catch (e) {}
})();
let llmCurrentVariantId = (function () {
  const input = document.querySelector('.llm-buybox-sidebar [name="id"]') || document.querySelector('[name="id"]');
  return input ? String(input.value) : null;
})();
// Returns {level, text} for the current variant's stock, or null when no badge should show.
function llmStockStatusFor(id) {
  const d = id != null ? llmInvData[String(id)] : null;
  if (!d || !d.t) return null;   // untracked → made-to-order, no stock badge
  const q = d.q;
  if (q <= 0) return null;       // out of stock → no "in stock" badge
  let level = 'in_stock';
  let label = 'In stock';
  if (q <= llmInvCfg.veryLow) { level = 'very_low'; label = 'Very low stock'; }
  else if (q <= llmInvCfg.low) { level = 'low'; label = 'Low stock'; }
  const showCount = level !== 'in_stock' || llmInvCfg.showCount === 'always';
  const text = label + (showCount ? ' (' + q + ' left)' : '');
  return { level: level, text: text };
}
// Update every stock badge on the page (standalone + Appstle) to the current variant's status.
// All DOM writes below are GUARDED (only touch the node when the value actually changes) — these
// badges live in Appstle cards watched by a MutationObserver (see llmScanAutoshipWidget), and an
// unconditional write would re-trigger the observer → scan → write → infinite loop.
function llmApplyStock(badge, s) {
  if (!s) { if (!badge.hidden) badge.hidden = true; return; }
  if (badge.hidden) badge.hidden = false;
  if (badge.getAttribute('data-inventory-level') !== s.level) badge.setAttribute('data-inventory-level', s.level);
  const span = badge.querySelector('span');
  if (span && span.textContent.trim() !== s.text) span.textContent = s.text;
}
function llmUpdateStockBadges() {
  const s = llmStockStatusFor(llmCurrentVariantId);
  document.querySelectorAll('.llm-option-stock-status').forEach(function (badge) { llmApplyStock(badge, s); });
}
document.addEventListener('on:variant:change', function (evt) {
  const variant = evt.detail && evt.detail.variant;
  if (variant && variant.id != null) llmCurrentVariantId = String(variant.id);
  llmUpdateStockBadges();
});

// CREATE-ONLY: this runs inside the MutationObserver-driven scan, so it must never write to an
// already-present badge (that would re-trigger the observer → scan → write → infinite loop / freeze).
// It only creates a badge when the current variant should show one and none exists yet; keeping an
// existing badge's TEXT in sync is done by llmUpdateStockBadges (load + on:variant:change only, NOT
// observer-driven). Appstle wipes the badge on every re-render, so the fresh create picks up the
// current variant's status.
function llmInjectStockStatus(card, afterSelector) {
  const anchor = card.querySelector(afterSelector);
  if (!anchor) return;
  if (card.querySelector('.llm-option-stock-status')) return; // present already → don't touch (no loop)
  const s = llmStockStatusFor(llmCurrentVariantId);
  if (!s) return; // untracked / out of stock → no badge
  const badge = document.createElement('div');
  badge.className = 'llm-option-stock-status';
  badge.setAttribute('data-inventory-level', s.level);
  badge.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const sp = document.createElement('span');
  sp.textContent = s.text;
  badge.appendChild(sp);
  anchor.insertAdjacentElement('afterend', badge);
}

/* Real bug found: the Autoship & Save box background stayed light
   coral even after switching back to One Time Purchase. This is
   Appstle's own widget's doing, not this project's CSS — confirmed
   via direct inspection that Appstle adds `appstle-active-option` to
   the newly-selected card on click, but never REMOVES
   `appstle_selected_background` from the previously-selected card, so
   both classes end up present on both cards simultaneously (whichever
   one's own CSS rule targets `appstle_selected_background`, that's
   the one that visually stays coral forever once selected once).
   Fixed by removing both classes from any card whose own radio isn't
   actually checked, every time the widget re-scans. */
function llmFixStaleSelectedBackground() {
  document.querySelectorAll('.appstle_subscription_wrapper_option').forEach(function (card) {
    const radio = card.querySelector('input[type="radio"]');
    if (radio && !radio.checked) {
      card.classList.remove('appstle_selected_background', 'appstle-active-option');
    }
  });
}

/* Appstle prints its per-unit price with Shopify's native ITEM label ("$X.XX/item").
   The rest of the store shows "/ea" (snippets/price.liquid), so relabel these two to match.
   Lives in the scan so it re-applies on every Appstle re-render; idempotent (once it's
   "/ea" there's no "/item" left to match, so it can't loop). */
function llmRelabelUnitPrice() {
  document.querySelectorAll('.appstle_one_time_unit_price, .appstle_selling_plan_unit_price').forEach(function (el) {
    if (/\/\s*items?\b/i.test(el.textContent)) {
      el.textContent = el.textContent.replace(/\/\s*items?\b/gi, '/ea');
    }
  });
}

/* The product-level "*This product can sometimes take longer than 3 business days to be delivered"
   footnote (gated on custom.free_1_3_day_shipping being non-blank) is rendered inside the
   .llm-product-shipping block — which is display:none on SUBSCRIPTION products, because their delivery
   ETA is injected into the Appstle option cards instead. That hid the footnote along with it. Surface a
   copy of it in the buy box (just below the Appstle widget) so it shows again. Only fires when the
   footnote actually exists (i.e. the metafield is set) and the Appstle widget is present, so it's a
   no-op on products that shouldn't show it. Idempotent via the --surfaced guard. */
function llmSurfaceShippingFootnote() {
  const src = document.querySelector('.llm-product-shipping:not(.llm-product-shipping--standalone) .llm-product-shipping__footnote');
  if (!src) return;
  // Place the footnote directly UNDER the "Estimated delivery" line — that line lives in each option
  // card's .llm-option-delivery mount. Insert as a SIBLING after it (not inside: .llm-option-delivery
  // is the .custom_message div whose innerHTML llm-shipping-eta.js rewrites on a 1s interval, which
  // would wipe a child). Add it to both cards; the non-selected card's copy is hidden by CSS
  // (llm-product-highlights-v2.css), so only the active card's shows — matching the delivery line's
  // own show/hide.
  document.querySelectorAll('.appstle_subscription_wrapper_option .llm-option-delivery').forEach(function (deliv) {
    const next = deliv.nextElementSibling;
    if (next && next.classList.contains('llm-shipping-footnote--surfaced')) return;
    const clone = src.cloneNode(true);
    clone.classList.add('llm-shipping-footnote--surfaced');
    deliv.insertAdjacentElement('afterend', clone);
  });
}

/* "200+ people have this on Autoship" inside the Autoship option card.
   The strongest proof we have for a consumable — it says people KEPT using it, not merely that many
   tried it — and it belongs where the subscribe decision is made rather than up with the price.

   The number comes from Liquid via a hidden span (custom.autoship_count, already floored to 200 for
   209 and only emitted at 25+), because this card is Appstle's markup injected at runtime and
   Liquid cannot write into it. Same approach as the delivery estimate and stock status above.

   Idempotent via the --mounted guard: llmScanAutoshipWidget re-runs on every Appstle re-render, and
   Appstle re-renders often. */
function llmInjectAutoshipCount(card) {
  const src = document.querySelector('.llm-autoship-count-src');
  if (!src) return;                                   // under 25 subscribers, or metafield unset
  const n = src.getAttribute('data-autoship-count');
  if (!n) return;
  if (card.querySelector('.llm-autoship-count--mounted')) return;

  const el = document.createElement('p');
  el.className = 'llm-autoship-count llm-autoship-count--mounted';
  // textContent, not innerHTML: the number is ours, but this sits inside a third party's DOM and
  // there is no reason for it to be able to carry markup.
  el.textContent = n + '+ people have this on Autoship';
  card.appendChild(el);
}

function llmScanAutoshipWidget() {
  document.querySelectorAll('.appstle_tooltip_wrapper_static').forEach(llmSetupAutoshipToggle);
  document.querySelectorAll('.appstle_subscribe_option_grid').forEach(llmSetupDeliveryDropdown);
  document.querySelectorAll('.appstle_subscribe_save_text').forEach(llmFixAutoshipLabelSpacing);
  document.querySelectorAll('.appstle_subscribe_save_text').forEach(llmInjectAutoshipIcon);
  document.querySelectorAll('.appstle-save-badge').forEach(llmMoveSaveBadgeUnderPrice);
  llmFixStaleSelectedBackground();
  llmRelabelUnitPrice();

  const optionCards = document.querySelectorAll('.appstle_subscription_wrapper_option');
  if (optionCards[0]) llmInjectOptionDelivery(optionCards[0], '.appstle_one_time_details_wrapper');
  if (optionCards[1]) llmInjectOptionDelivery(optionCards[1], '.appstle_subscription_radio_wrapper');
  llmSurfaceShippingFootnote(); // after the delivery mounts exist, so it can anchor under them
  if (optionCards[0]) llmInjectStockStatus(optionCards[0], '.appstle_one_time_price_wrapper');
  if (optionCards[1]) llmInjectStockStatus(optionCards[1], '.appstle_subscription_amount_wrapper');
  if (optionCards[1]) llmInjectAutoshipBenefits(optionCards[1], document.querySelector('.appstle_tooltip_wrapper_static'));
  // Last, so it lands at the BOTTOM of the card — under the price, the delivery line and the
  // benefits list, where it reads as a closing reassurance rather than a headline.
  if (optionCards[1]) llmInjectAutoshipCount(optionCards[1]);
}

llmScanAutoshipWidget();
llmUpdateStockBadges();

const llmAutoshipObserver = new MutationObserver(llmScanAutoshipWidget);
llmAutoshipObserver.observe(document.body, { childList: true, subtree: true });
